/**
 * Native Shopify Swatch Picker JavaScript
 * Handles variant selection through visual swatches using Shopify's native swatch system
 */
(function() {
  'use strict';

  class SwatchPicker {
    constructor(container) {
      this.container = container;
      this.productId = this.container.closest('[data-product-id]')?.dataset.productId;
      this.swatchOptions = this.container.querySelectorAll('.swatch-option');
      this.hiddenSelects = this.container.querySelectorAll('.swatch-hidden-select');
      this.currentVariant = null;
      
      this.init();
    }

    init() {
      this.bindEvents();
      this.updateSwatchStates();
      this.syncWithHiddenSelects();
    }

    bindEvents() {
      // Bind click events to swatch options
      this.swatchOptions.forEach(swatch => {
        swatch.addEventListener('click', (e) => this.handleSwatchClick(e));
        swatch.addEventListener('keydown', (e) => this.handleSwatchKeydown(e));
      });

      // Listen for variant changes from other sources
      document.addEventListener('variant:change', (e) => this.handleVariantChange(e));
      
      // Listen for form submission to sync hidden selects
      const form = this.container.closest('form');
      if (form) {
        form.addEventListener('submit', (e) => this.handleFormSubmit(e));
      }
    }

    handleSwatchClick(event) {
      const swatch = event.currentTarget;
      
      if (swatch.disabled) {
        return;
      }

      const optionName = swatch.dataset.option;
      const optionValue = swatch.dataset.value;
      const optionIndex = parseInt(swatch.dataset.optionIndex);

      // Update visual state
      this.updateSwatchSelection(optionName, optionValue);
      
      // Update hidden selects
      this.updateHiddenSelects(optionName, optionValue);
      
      // Trigger variant change
      this.triggerVariantChange();
      
      // Update product image if available
      this.updateProductImage(swatch);
      
      // Update URL if needed
      this.updateURL(optionName, optionValue);
    }

    handleSwatchKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.handleSwatchClick(event);
      }
    }

    handleVariantChange(event) {
      if (event.detail && event.detail.variant) {
        this.currentVariant = event.detail.variant;
        this.updateSwatchStates();
      }
    }

    handleFormSubmit(event) {
      // Ensure hidden selects are synced before form submission
      this.syncWithHiddenSelects();
    }

    updateSwatchSelection(optionName, selectedValue) {
      // Remove selected state from all swatches in this option group
      const optionGroup = this.container.querySelector(`[data-option="${optionName}"]`);
      const swatchesInGroup = optionGroup.querySelectorAll('.swatch-option');
      
      swatchesInGroup.forEach(swatch => {
        swatch.classList.remove('swatch-option--selected');
      });

      // Add selected state to clicked swatch
      const selectedSwatch = optionGroup.querySelector(`[data-value="${selectedValue}"]`);
      if (selectedSwatch) {
        selectedSwatch.classList.add('swatch-option--selected');
      }
    }

    updateHiddenSelects(optionName, selectedValue) {
      const hiddenSelect = this.container.querySelector(`select[data-option="${optionName}"]`);
      if (hiddenSelect) {
        const option = hiddenSelect.querySelector(`option[value="${selectedValue}"]`);
        if (option) {
          option.selected = true;
        }
      }
    }

    updateSwatchStates() {
      if (!this.currentVariant) return;

      // Update swatch states based on current variant
      this.swatchOptions.forEach(swatch => {
        const optionName = swatch.dataset.option;
        const optionValue = swatch.dataset.value;
        const optionIndex = parseInt(swatch.dataset.optionIndex);
        
        // Check if this swatch matches the current variant
        const isSelected = this.currentVariant.options[optionIndex] === optionValue;
        
        if (isSelected) {
          swatch.classList.add('swatch-option--selected');
        } else {
          swatch.classList.remove('swatch-option--selected');
        }
      });
    }

    updateProductImage(swatch) {
      const swatchImage = swatch.querySelector('.swatch-image');
      if (!swatchImage) return;

      const imageUrl = swatchImage.src;
      if (!imageUrl) return;

      // Find the main product image container
      const productGallery = document.querySelector('.product-gallery, .product-media');
      if (!productGallery) return;

      // Look for the main image
      const mainImage = productGallery.querySelector('.main img, .product-image img, .rimage__image');
      if (mainImage) {
        // Add loading state
        mainImage.style.opacity = '0.7';
        
        // Create new image to preload
        const newImage = new Image();
        newImage.onload = () => {
          mainImage.src = imageUrl;
          mainImage.style.opacity = '1';
        };
        newImage.src = imageUrl;
      }
    }

    updateURL(optionName, optionValue) {
      // Update URL parameters for better UX and sharing
      if (window.history && window.history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set(optionName, optionValue);
        window.history.replaceState({}, '', url);
      }
    }

    syncWithHiddenSelects() {
      // Ensure hidden selects reflect current swatch selection
      this.swatchOptions.forEach(swatch => {
        if (swatch.classList.contains('swatch-option--selected')) {
          const optionName = swatch.dataset.option;
          const optionValue = swatch.dataset.value;
          this.updateHiddenSelects(optionName, optionValue);
        }
      });
    }

    triggerVariantChange() {
      // Create custom event for variant change
      const event = new CustomEvent('variant:change', {
        detail: {
          swatchPicker: this,
          container: this.container
        }
      });
      
      document.dispatchEvent(event);
    }

    // Public method to get current selection
    getCurrentSelection() {
      const selection = {};
      this.swatchOptions.forEach(swatch => {
        if (swatch.classList.contains('swatch-option--selected')) {
          const optionName = swatch.dataset.option;
          const optionValue = swatch.dataset.value;
          selection[optionName] = optionValue;
        }
      });
      return selection;
    }

    // Public method to set selection programmatically
    setSelection(optionName, value) {
      const swatch = this.container.querySelector(`[data-option="${optionName}"][data-value="${value}"]`);
      if (swatch && !swatch.disabled) {
        this.handleSwatchClick({ currentTarget: swatch });
      }
    }

    // Method to check if a variant is available
    isVariantAvailable(optionSelections) {
      // This would need to be implemented based on your product data structure
      // For now, we'll assume all variants are available
      return true;
    }
  }

  // Initialize all swatch pickers when DOM is ready
  function initSwatchPickers() {
    const containers = document.querySelectorAll('.swatch-selectors');
    containers.forEach(container => {
      new SwatchPicker(container);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSwatchPickers);
  } else {
    initSwatchPickers();
  }

  // Also initialize when new content is loaded (for dynamic content)
  document.addEventListener('shopify:section:load', (event) => {
    const containers = event.target.querySelectorAll('.swatch-selectors');
    containers.forEach(container => {
      new SwatchPicker(container);
    });
  });

  // Export for global use if needed
  window.SwatchPicker = SwatchPicker;
})();
